import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import {
  isCurrentProjectBuildy,
  isCurrentProjectCitizen,
  isCurrentProjectIntranet,
  isCurrentProjectStimull
} from 'src/util/projectCheckers';
import { getUserRoleIsUser } from 'src/util/isCurrentUserRoot';
import { isDomainsEnabled } from '../../util/flags';
import { getProjectProvidersAction } from '../../actions/ProviderActions';

const useNavigationData = () => {
  const dispatch = useDispatch();
  const features = useSelector(({ project }) => project.currentProject?.features);
  const hasDomains = isDomainsEnabled(features);
  const currentUser = useSelector(({ login }) => login.user);
  const providers = useSelector(({ provider }) => provider.providers);
  const customTypes = useSelector(({ customType }) => customType.customTypes);
  const currentProject = useSelector(({ project }) => project.currentProject);

  const contentMenuTags = useMemo(() => {
    const tags = [];

    customTypes.forEach((item) => {
      if (item.tags && item.tags.includes('menu')) {
        tags.push({
          isVisible: true,
          name: item.name,
          url: `/content?filter=${item._id}`
        });
      }
    });

    return tags;
  }, [customTypes]);

  useEffect(() => {
    if (currentProject) {
      dispatch(getProjectProvidersAction());
    }
  }, [currentProject, dispatch]);

  const resolvedProviders = providers.map((el) => el.toLowerCase());

  // check for stimull and buildy specific cases
  const checkForSpecificCases = () => {
    let isVisible = true;

    if (isCurrentProjectStimull) {
      isVisible = false;
    } else if (isCurrentProjectBuildy) {
      if (getUserRoleIsUser()) {
        isVisible = false;
      } else {
        isVisible = true;
      }
    }

    return isVisible;
  };

  const checkForSpecificBuildyCases = () => {
    let isVisible = true;

    if (isCurrentProjectBuildy) {
      if (getUserRoleIsUser()) {
        isVisible = false;
      } else {
        isVisible = true;
      }
    }

    return isVisible;
  };

  return useMemo(
    () => [
      {
        id: 'domain',
        isVisible: resolvedProviders.includes('domain') && checkForSpecificBuildyCases(),
        name: 'Domains',
        url: '/domains'
      },
      {
        children: [
          {
            id: 'resources',
            isVisible: true,
            name: 'Resources',
            url: '/resources'
          },
          { id: 'cPanel', isVisible: true, name: 'cPanel', url: '/cpanel' },
          { id: 'email', isVisible: true, name: 'Email', url: '/email' }
        ],
        id: 'hosting',
        isVisible: resolvedProviders.includes('hosting') && checkForSpecificBuildyCases(),
        name: 'Hosting'
      },
      {
        children: [
          { id: 'providers', isVisible: true, name: 'Providers', url: '/settings/providers' },
          { id: 'deployment', isVisible: true, name: 'Deployment', url: '/settings/deployment' },
          {
            id: 'dynamic links config',
            isVisible: resolvedProviders.includes('dynamic links'),
            name: 'Dynamic Links Config',
            url: '/settings/dynamic-links-config'
          }
        ],
        id: 'settings',
        isVisible: checkForSpecificBuildyCases(),
        name: 'Settings'
      },
      {
        children: [
          {
            id: 'customers',
            isVisible: true,
            name: 'Customers',
            url: '/authentication/customers'
          },
          {
            id: 'complains',
            isVisible: currentProject?.name === 'Dade',
            name: 'Complains',
            url: '/authentication/complains'
          }
        ],
        id: 'authorization',
        isVisible: resolvedProviders.includes('authorization') && checkForSpecificBuildyCases(),
        name: 'Authentication'
      },
      {
        id: 'email-subscription',
        isVisible:
          resolvedProviders.includes('email-subscription') && checkForSpecificBuildyCases(),
        name: 'Email Subscriptions',
        url: '/email-subscription'
      },
      {
        id: 'email-notification',
        isVisible:
          resolvedProviders.includes('email notifications') && checkForSpecificBuildyCases(),
        name: 'Email Notifications',
        url: '/email-notification'
      },
      {
        id: 'text-notification',
        isVisible:
          resolvedProviders.includes('text notifications') && checkForSpecificBuildyCases(),
        name: 'Text Notifications',
        url: '/text-notification'
      },
      {
        children: [
          { id: 'content-model', isVisible: true, name: 'Content Model', url: '/content-model' },
          {
            children: contentMenuTags,
            id: 'content',
            isVisible: true,
            name: 'Content',
            url: '/content'
          },
          {
            id: 'content-review',
            isVisible: currentUser.role !== 'user',
            name: 'Content Review',
            url: '/content-review'
          },
          { id: 'files', isVisible: true, name: 'Files', url: '/files' },
          { id: 'images', isVisible: true, name: 'Images', url: '/images' },
          { id: 'language', isVisible: true, name: 'Language', url: '/language' },
          {
            id: 'highlighted content',
            isVisible: true,
            name: 'Highlighted Contents',
            url: '/highlight'
          }
        ],
        id: 'cms',
        isVisible: checkForSpecificBuildyCases(),
        name: 'CMS'
      },
      {
        id: 'balance',
        isVisible: resolvedProviders.includes('vacation balance') && checkForSpecificBuildyCases(),
        name: 'Vacation Balance',
        url: '/balance'
      },
      {
        children: [
          {
            id: 'trainings',
            isVisible: isCurrentProjectIntranet,
            name: 'Trainings',
            url: '/requestsActions/trainings'
          },
          {
            id: 'vacations',
            isVisible: isCurrentProjectIntranet,
            name: 'Vacations',
            url: '/requestsActions/vacations'
          },
          {
            id: 'requests',
            isVisible: true,
            name: 'Requests',
            url: '/requestsActions/requests'
          }
        ],
        id: 'requestActions',
        isVisible: resolvedProviders.includes('request actions') && checkForSpecificBuildyCases(),
        name: 'Requests Actions'
      },
      {
        id: 'events',
        isVisible: resolvedProviders.includes('events') && checkForSpecificBuildyCases(),
        name: 'events',
        url: '/events'
      },
      {
        id: 'box',
        isVisible: resolvedProviders.includes('box') && checkForSpecificBuildyCases(),
        name: 'Box',
        url: '/box'
      },
      {
        id: 'users',
        isVisible: !getUserRoleIsUser() && checkForSpecificBuildyCases(),
        name: 'Users',
        url: '/users'
      },
      {
        id: 'claims',
        isVisible: resolvedProviders.includes('claims') && checkForSpecificBuildyCases(),
        name: 'Claims',
        url: '/claims'
      },
      {
        id: 'notifications',
        isVisible: resolvedProviders.includes('notifications') && checkForSpecificBuildyCases(),
        name: 'Notifications',
        url: '/notifications'
      },
      {
        id: 'push notifications',
        isVisible:
          resolvedProviders.includes('push notifications') && checkForSpecificBuildyCases(),
        name: 'Push Notifications',
        url: '/push-notifications'
      },
      {
        id: 'chat',
        isVisible: resolvedProviders.includes('chat') && checkForSpecificBuildyCases(),
        name: 'Chat',
        url: 'https://next.mdello.com/conversations'
      },

      {
        children: [
          {
            id: 'providers',
            isVisible: !isCurrentProjectStimull && checkForSpecificBuildyCases(),
            name: 'Providers',
            url: '/payments/providers'
          },
          {
            id: 'transactions',
            isVisible: !isCurrentProjectStimull && checkForSpecificBuildyCases(),
            name: 'Transactions',
            url: '/payments/transactions'
          },
          {
            id: 'transactions-v2',
            isVisible: resolvedProviders.includes('payments v2') && checkForSpecificBuildyCases(),
            name: 'Transactions v2',
            url: 'https://next.mdello.com/transactions'
          },
          {
            id: 'ipay',
            isVisible: resolvedProviders.includes('ipay') && checkForSpecificBuildyCases(),
            name: 'iPay',
            url: '/ipay'
          },
          {
            id: 'zohodeals',
            isVisible: resolvedProviders.includes('zoho deals') && checkForSpecificBuildyCases(),
            name: 'zohoDeals',
            url: '/payments/zohodeals'
          },
          {
            id: 'maintenance',
            isVisible: isCurrentProjectCitizen,
            name: 'Maintenance',
            url: '/payments/maintenance'
          },
          {
            id: 'payment subscriptions',
            isVisible:
              resolvedProviders.includes('payment subscriptions') && checkForSpecificBuildyCases(),
            name: 'Subscription',
            url: '/payment-subscriptions'
          },
          {
            id: 'payze',
            isVisible: resolvedProviders.includes('payze') && checkForSpecificBuildyCases(),
            name: 'Payze',
            url: '/payze'
          },
          {
            children: [
              {
                id: 'MycoinsTransactions',
                isVisible: true,
                name: 'transactions',
                url: '/payments/mycoins/transactions'
              }
            ],
            id: 'MyCoins',
            isVisible: resolvedProviders.includes('mycoins') && checkForSpecificBuildyCases(),
            name: 'MyCoins'
          }
        ],
        id: 'payment',
        isVisible: resolvedProviders.includes('payment') && checkForSpecificBuildyCases(),
        name: 'Payments',
        url: '/payments'
      },
      {
        id: 'reports',
        isVisible: resolvedProviders.includes('reports') && checkForSpecificBuildyCases(),
        name: 'Reports',
        url: '/reports'
      },
      {
        children: [
          {
            id: 'sellers',
            isVisible: isCurrentProjectBuildy && !getUserRoleIsUser(),
            name: 'Sellers',
            url: '/ecommerce/sellers'
          },
          {
            id: 'companies',
            isVisible: isCurrentProjectBuildy && !getUserRoleIsUser(),
            name: 'Companies',
            url: '/ecommerce/companies'
          },
          {
            id: 'ordersGroup',
            isVisible: isCurrentProjectBuildy && !getUserRoleIsUser(),
            name: 'Orders Group',
            url: '/ecommerce/orders-group'
          },
          {
            id: 'orders',
            isVisible: true,
            name: 'Orders',
            url: '/ecommerce/orders'
          },
          {
            id: 'delivery',
            isVisible: checkForSpecificCases(),
            name: 'Delivery',
            url: '/ecommerce/delivery'
          },
          {
            id: 'categories',
            isVisible: checkForSpecificBuildyCases(),
            name: 'Categories',
            url: '/ecommerce/categories'
          },
          {
            id: 'items',
            isVisible: true,
            name: 'Items',
            url: '/ecommerce/items'
          },
          {
            id: 'reviews',
            isVisible: checkForSpecificBuildyCases(),
            name: 'Reviews',
            url: '/ecommerce/reviews'
          },
          {
            id: 'comments',
            isVisible: checkForSpecificCases(),
            name: 'Comments',
            url: '/ecommerce/comments'
          }
        ],
        id: 'e-commerce',
        isVisible: resolvedProviders.includes('e-commerce'),
        name: 'eCommerce'
      },

      {
        id: 'dynamic links',
        isVisible: resolvedProviders.includes('dynamic links') && checkForSpecificBuildyCases(),
        name: 'Dynamic Links',
        url: '/dynamic-links'
      },
      {
        id: 'projects',
        isVisible: checkForSpecificBuildyCases(),
        name: 'Projects',
        url: '/projects'
      },
      {
        children: [
          {
            id: 'payment-method',
            isVisible: true,
            name: 'Payment Methods',
            url: '/payment-method'
          },
          {
            id: 'invoices',
            isVisible: true,
            name: 'Invoices',
            url: '/invoices'
          }
        ],
        id: 'billing',
        isVisible: hasDomains && checkForSpecificBuildyCases(),
        name: 'Billing'
      },
      {
        id: 'trips',
        isVisible: resolvedProviders.includes('sait') && checkForSpecificBuildyCases(),
        name: 'Trips',
        url: '/trips'
      }
    ],
    [contentMenuTags, currentUser.role, hasDomains, resolvedProviders]
  );
};

export default useNavigationData;
